import React, {useRef, useState} from 'react';
import AppButton from '../../../../../../../components/common/AppButton';
import {SVG} from '../../../../../../../constants/assetLists';
import AppMenu from '../../../../../../../components/common/AppMenu';
import ImportExcelOptionsDialog from '../../ImportExcelOptionsDialog';
import * as XLSX from 'xlsx';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import {useUsersContext} from '../../../../../../../context/users/UsersContextProvider';
import {useMutation} from '@tanstack/react-query';
import {handleCreateBulkUserService} from '../../../../../../../services/users';

const IMPORT_MENUITEMS = [
  {
    title: 'יבוא משתמשים מקובץ אקסל',
    value: 'import',
    icon: SVG.excelGreenIcon,
  },
];

function parseSheet(ws: XLSX.WorkSheet) {
  // 1) Rows as arrays
  const rows = XLSX.utils.sheet_to_json(ws, {header: 1, defval: ''}) as any[][];

  // 2) Remove leading/trailing empty rows
  const nonEmptyRows = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonEmptyRows.length) return [];

  // 3) If first row is that "Table 1" junk, drop it
  const junkFirstRow = nonEmptyRows[0].every(
    (c, i) =>
      (i === 0 && String(c).trim().toLowerCase().includes('table')) ||
      String(c).trim() === '',
  );
  const headerRow = junkFirstRow ? (nonEmptyRows[1] ?? []) : nonEmptyRows[0];
  const dataRows = junkFirstRow ? nonEmptyRows.slice(2) : nonEmptyRows.slice(1);

  // 4) Normalize headers and keep only non-empty columns
  const rawHeaders = headerRow.map(h => String(h).trim());
  const keepIdx: number[] = [];
  const headers = rawHeaders
    .map((h, i) => {
      if (h) keepIdx.push(i);
      return h;
    })
    .filter(Boolean);

  // 5) Map data rows to objects using only kept columns
  const json = dataRows.map(r => {
    const obj: Record<string, any> = {};
    keepIdx.forEach((colIdx, j) => {
      obj[headers[j]] = r[colIdx] ?? '';
    });
    return obj;
  });

  return json;
}

function AddUsers() {
  const {showSnackbar} = useSnackbar();

  const {setShowAddEditDialog, refetchAllUsers} = useUsersContext();

  const [importAnchorTaskEl, setImportAnchorTaskEl] =
    useState<HTMLElement | null>(null);
  const [importedUsers, setImportedUsers] = useState<any>(null);
  const [showExcelOptionsDialog, setShowExcelOptionsDialog] = useState(false);
  const [isDuplicateErr, setIsDuplicateErr] = useState(false);

  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleImportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImportAnchorTaskEl(event.currentTarget as HTMLElement);
  };

  const handleModalOpen = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowAddEditDialog(true);
  };

  const handleImportMenuClose = () => {
    setImportAnchorTaskEl(null);
  };

  const handleImportMenuItemClick = (val: string) => {
    if (val === 'import') {
      excelInputRef.current?.click();
    }
  };

  const isValidData = (jsonData: any) => {
    const keys = Object.keys(jsonData[0]);
    if (
      keys.length > 0 &&
      keys.includes('firstName') &&
      keys.includes('lastName') &&
      keys.includes('clientId') &&
      keys.includes('branchId') &&
      keys.includes('role') &&
      keys.includes('departmentId') &&
      keys.includes('associatedGroups') &&
      keys.includes('countryCode') &&
      keys.includes('phone') &&
      keys.includes('email') &&
      keys.includes('notifyBy') &&
      keys.includes('associatedGroups') &&
      keys.includes('birthdate')
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleExcelPicked: React.ChangeEventHandler<
    HTMLInputElement
  > = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async e => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {type: 'array'});
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = parseSheet(worksheet);

        if (isValidData(jsonData)) {
          // Validate each user before submitting
          const validUsers = jsonData.filter((user: any) => {
            const emailExists =
              jsonData.filter(
                (u: any) =>
                  u.email?.trim().toLowerCase() ===
                  user.email?.trim().toLowerCase(),
              ).length > 1;

            if (
              user?.firstName &&
              user?.lastName &&
              user?.clientId &&
              user?.branchId &&
              user?.role &&
              user?.departmentId &&
              user?.countryCode &&
              user?.phone &&
              user?.email &&
              !emailExists
            ) {
              return true;
            } else {
              return false;
            }
          });

          if (validUsers.length === 0) {
            showSnackbar({
              type: 'error',
              message: 'No valid users found in the file.',
            });
            return;
          }

          const parseCommaSeparated = (val: any): string[] => {
            if (!val) return [];
            return String(val)
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
          };

          setImportedUsers(() =>
            validUsers?.map((user: any) => {
              const {
                clientId,
                branchId,
                subBranchId,
                defaultBranchId,
                associatedGroups,
                countryCode,
                phone,
                role,
                departmentId,
                ...rest
              } = user ?? {};
              return {
                ...rest,
                phone: `${countryCode}${phone}`,
                status: 'active',
                roles: [role?.trim()],
                clientId: parseCommaSeparated(clientId),
                branchId: parseCommaSeparated(branchId),
                subBranchId: parseCommaSeparated(subBranchId),
                ...(defaultBranchId
                  ? {defaultBranchId: String(defaultBranchId).trim()}
                  : {}),
                associatedGroups: parseCommaSeparated(user?.associatedGroups),
                departmentId:
                  String(departmentId).trim().toLowerCase() === 'all'
                    ? 'all'
                    : parseCommaSeparated(departmentId),
              };
            }),
          );
          setShowExcelOptionsDialog(true);
        } else {
          showSnackbar({
            type: 'error',
            message: 'Invalid file format',
          });
          return;
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      console.error(err);
    } finally {
      e.target.value = '';
    }
  };

  const handleOptionClick = (opt: string) => {
    const payload = {
      users: importedUsers,
      duplicateAction: opt,
    };
    handleBulkCreateUser(payload);
  };

  const handleExcelDialogClose = () => {
    setImportedUsers(null);
    setIsDuplicateErr(false);
    setShowExcelOptionsDialog(false);
  };

  const {mutate: handleBulkCreateUser, isPending: isAdding} = useMutation({
    mutationFn: handleCreateBulkUserService,
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      refetchAllUsers();
      handleExcelDialogClose();
    },
    onError: (error: any) => {
      // Check if this is a duplicate error (409 Conflict)
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.isDuplicateError
      ) {
        // Show dialog with only replace/skip options
        setIsDuplicateErr(true);
        // Keep dialog open, don't close
      } else {
        // Other errors - show snackbar and close
        showSnackbar({
          message: error?.response?.data?.message ?? 'שגיאה בייבוא ​​משתמשים',
          type: 'error',
        });
        handleExcelDialogClose();
      }
    },
  });

  return (
    <>
      <div>
        <AppButton
          variant="primary"
          py="12px"
          iconStart={
            <div className="w-[12px] h-[12px]">
              <img
                src={SVG.addWhiteIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
          }
          iconEnd={
            <AppMenu
              anchorEl={importAnchorTaskEl}
              showDivider
              dividerIndex={1}
              itemIconWidth={24}
              itemIconHeight={24}
              open={Boolean(importAnchorTaskEl)}
              onOpen={handleImportMenuOpen}
              onClose={handleImportMenuClose}
              items={IMPORT_MENUITEMS}
              onItemClick={handleImportMenuItemClick}
              menuIcon={
                <div className="w-[12px] h-[6px]">
                  <img
                    src={SVG.dropdownWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
            />
          }
          onClick={handleModalOpen}>
          הוספה
        </AppButton>
        {/* Hidden file input for import excel */}
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleExcelPicked}
        />
      </div>

      <ImportExcelOptionsDialog
        open={showExcelOptionsDialog}
        onClose={handleExcelDialogClose}
        title="יבוא משתמשים מאקסל"
        isDuplicateErr={isDuplicateErr}
        isLoading={isAdding}
        onOptionClick={handleOptionClick}
      />
    </>
  );
}

export default AddUsers;
